import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import type { ContactPolicy, UpdateContactPolicyInput } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SaveButton } from '@/components/custom/save-button';
import { useContactPolicy, useUpdateContactPolicy } from '@/hooks/use-outreach';

/**
 * The organization's contact policy — the rules across every campaign.
 *
 * Campaigns say *who* and *what*. This says *how much, how often, and who
 * wins*, once. It is the widest blast radius in the system: it caps every
 * campaign at the same time and the kill switch lives here.
 */

interface NumberFieldProps {
  id: keyof UpdateContactPolicyInput & string;
  label: string;
  help?: string;
  unit: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
}

function NumberField({
  id,
  label,
  help,
  unit,
  min,
  max,
  value,
  onChange,
}: NumberFieldProps): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-6 py-2">
      <div className="min-w-0">
        <Label htmlFor={id} className="font-normal">
          {label}
        </Label>
        {help && <p className="mt-0.5 text-xs text-muted-foreground">{help}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Input
          id={id}
          type="number"
          className="w-24"
          min={min}
          max={max}
          value={value}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
        />
        <span className="w-16 text-sm text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}

/** Draft state: every field, so a partial edit never sends a partial policy. */
type Draft = Pick<
  ContactPolicy,
  | 'enabled'
  | 'quietHoursStart'
  | 'quietHoursEnd'
  | 'contactCooldownMinutes'
  | 'maxContactsPerPatient'
  | 'maxContactsPerDays'
  | 'maxConcurrentCalls'
  | 'dailyCallCap'
  | 'voicemailGraceMinutes'
  | 'attributionWindowDays'
  | 'reEntryCooldownDays'
  | 'maxCallbackHonors'
>;

function toDraft(policy: ContactPolicy): Draft {
  const {
    enabled,
    quietHoursStart,
    quietHoursEnd,
    contactCooldownMinutes,
    maxContactsPerPatient,
    maxContactsPerDays,
    maxConcurrentCalls,
    dailyCallCap,
    voicemailGraceMinutes,
    attributionWindowDays,
    reEntryCooldownDays,
    maxCallbackHonors,
  } = policy;
  return {
    enabled,
    quietHoursStart,
    quietHoursEnd,
    contactCooldownMinutes,
    maxContactsPerPatient,
    maxContactsPerDays,
    maxConcurrentCalls,
    dailyCallCap,
    voicemailGraceMinutes,
    attributionWindowDays,
    reEntryCooldownDays,
    maxCallbackHonors,
  };
}

export function ContactPolicyEditor({
  onClose,
  readOnly = false,
}: {
  onClose: () => void;
  /** Readable with outreach:view, changeable with outreach:edit (ADR-318, H). */
  readOnly?: boolean;
}): React.ReactElement {
  const { data: policy, isLoading } = useContactPolicy();
  const save = useUpdateContactPolicy();
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (policy) setDraft((current) => current ?? toDraft(policy));
  }, [policy]);

  if (isLoading || !policy || !draft) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const set = (patch: Partial<Draft>): void => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(policy));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Back to campaigns">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">Contact policy</h1>
            <p className="text-xs text-muted-foreground">Applies to every campaign at once.</p>
          </div>
        </div>
        {readOnly ? (
          <span className="text-sm text-muted-foreground">
            Only someone who can edit campaigns can change these.
          </span>
        ) : (
          <SaveButton
            isPending={save.isPending}
            disabled={!dirty}
            type="button"
            onClick={() => save.mutate(draft)}
          >
            Save
          </SaveButton>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* A disabled fieldset disables every control inside it, the switches included. */}
        <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
          <div className="mx-auto max-w-2xl space-y-8">
            {/* ── Kill switch ────────────────────────────────────────── */}
            <section className="rounded-md border p-4">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <Label htmlFor="policy-enabled">Automated calling</Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Turn this off and everything holds where it is. No run is lost and nothing is
                    reset — attempts simply stop being placed.
                  </p>
                </div>
                <Switch
                  id="policy-enabled"
                  checked={draft.enabled}
                  onCheckedChange={(enabled) => set({ enabled })}
                />
              </div>
            </section>

            {/* ── Calling hours ──────────────────────────────────────── */}
            <section className="space-y-3">
              <h2 className="text-sm font-medium">Calling hours</h2>
              <div className="flex items-center gap-2">
                <Label htmlFor="quiet-start" className="w-28 font-normal">
                  Never before
                </Label>
                <Input
                  id="quiet-start"
                  type="time"
                  className="w-32"
                  value={draft.quietHoursStart}
                  onChange={(e) => set({ quietHoursStart: e.target.value })}
                />
                <Label htmlFor="quiet-end" className="ml-4 w-24 font-normal">
                  Never after
                </Label>
                <Input
                  id="quiet-end"
                  type="time"
                  className="w-32"
                  value={draft.quietHoursEnd}
                  onChange={(e) => set({ quietHoursEnd: e.target.value })}
                />
              </div>
              {/*
              Rendered as a locked fact rather than a disabled input: the floor
              is not a setting that happens to be unavailable, it is enforced in
              code and a narrower value is the only thing this screen can send.
            */}
              <p className="flex items-start gap-1.5 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {policy.quietHoursFloor.start}–{policy.quietHoursFloor.end} is enforced in code.
                  You may narrow this, never widen it — a wider value is refused rather than quietly
                  clamped. Campaigns narrow it further still.
                </span>
              </p>
            </section>

            {/* ── Per patient ────────────────────────────────────────── */}
            <section className="space-y-1 border-t pt-6">
              <h2 className="mb-2 text-sm font-medium">How often we may contact one patient</h2>
              <NumberField
                id="contactCooldownMinutes"
                label="Cooldown between contacts"
                help="Across every campaign, not per campaign."
                unit="minutes"
                min={0}
                max={20160}
                value={draft.contactCooldownMinutes}
                onChange={(contactCooldownMinutes) => set({ contactCooldownMinutes })}
              />
              <NumberField
                id="maxContactsPerPatient"
                label="Ceiling on contacts"
                unit="contacts"
                min={1}
                max={50}
                value={draft.maxContactsPerPatient}
                onChange={(maxContactsPerPatient) => set({ maxContactsPerPatient })}
              />
              <NumberField
                id="maxContactsPerDays"
                label="…measured over"
                unit="days"
                min={1}
                max={365}
                value={draft.maxContactsPerDays}
                onChange={(maxContactsPerDays) => set({ maxContactsPerDays })}
              />
              <NumberField
                id="voicemailGraceMinutes"
                label="Wait after leaving a voicemail"
                help="So we do not call again while the patient may be about to call back."
                unit="minutes"
                min={0}
                max={10080}
                value={draft.voicemailGraceMinutes}
                onChange={(voicemailGraceMinutes) => set({ voicemailGraceMinutes })}
              />
              <NumberField
                id="reEntryCooldownDays"
                label="Wait before re-entering a campaign"
                help="A patient who qualifies again after finishing does not restart immediately."
                unit="days"
                min={0}
                max={365}
                value={draft.reEntryCooldownDays}
                onChange={(reEntryCooldownDays) => set({ reEntryCooldownDays })}
              />
              <NumberField
                id="maxCallbackHonors"
                label="Callbacks honoured per run"
                help="Bounds a patient who keeps asking to be called later and never answers."
                unit="callbacks"
                min={0}
                max={20}
                value={draft.maxCallbackHonors}
                onChange={(maxCallbackHonors) => set({ maxCallbackHonors })}
              />
            </section>

            {/* ── Capacity ───────────────────────────────────────────── */}
            <section className="space-y-1 border-t pt-6">
              <h2 className="mb-2 text-sm font-medium">Capacity</h2>
              <NumberField
                id="maxConcurrentCalls"
                label="Calls at once"
                unit="calls"
                min={1}
                max={100}
                value={draft.maxConcurrentCalls}
                onChange={(maxConcurrentCalls) => set({ maxConcurrentCalls })}
              />
              <NumberField
                id="dailyCallCap"
                label="Calls per day"
                unit="calls"
                min={1}
                max={10000}
                value={draft.dailyCallCap}
                onChange={(dailyCallCap) => set({ dailyCallCap })}
              />
              <p className="pt-1 text-xs text-muted-foreground">
                Outreach yields a line to a patient calling in.
              </p>
            </section>

            {/* ── Attribution ────────────────────────────────────────── */}
            <section className="space-y-1 border-t pt-6">
              <h2 className="mb-2 text-sm font-medium">Attribution</h2>
              <NumberField
                id="attributionWindowDays"
                label="Credit a return call within"
                help="A patient calling us back after a voicemail is credited to the campaign that called them. Outside the window it is recorded as organic — under-crediting beats over-crediting."
                unit="days"
                min={1}
                max={90}
                value={draft.attributionWindowDays}
                onChange={(attributionWindowDays) => set({ attributionWindowDays })}
              />
            </section>

            <section className="rounded-md border bg-muted/40 p-4 text-sm">
              <div className="font-medium">Always respected</div>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>Do-not-contact and channel opt-outs, on every campaign.</li>
                <li>A patient's stated preferred time.</li>
                <li>At most one outbound call to a patient at a time, across all campaigns.</li>
              </ul>
            </section>
          </div>
        </fieldset>
      </div>
    </div>
  );
}
